import React from 'react'

import {AttendanceSummary} from '../../../../modes/attendance/attendanceV2Types'
import AttendancePlainCard from '../AttendancePlainCard'

type AttendanceV2SummaryKpisProps = {
  summary: AttendanceSummary
  theme: 'light' | 'dark'
  rowCount?: number
}

function formatPercent(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${Math.round(value * 10) / 10}%`
}

function hasPositiveCount(value: number | undefined): boolean {
  return value != null && Number.isFinite(value) && value > 0
}

function hasMeaningfulStatusCounts(summary: AttendanceSummary): boolean {
  return (
    hasPositiveCount(summary.present) ||
    hasPositiveCount(summary.absent) ||
    hasPositiveCount(summary.late) ||
    hasPositiveCount(summary.leave) ||
    hasPositiveCount(summary.bunk) ||
    hasPositiveCount(summary.other) ||
    hasPositiveCount(summary.totalStudents)
  )
}

function buildSummaryPairs(
  summary: AttendanceSummary,
  rowCount = 0
): {metric: string; value: string}[] {
  const pairs: {metric: string; value: string}[] = []

  if (hasPositiveCount(summary.present)) {
    pairs.push({metric: 'Present', value: String(summary.present)})
  }
  if (hasPositiveCount(summary.absent)) {
    pairs.push({metric: 'Absent', value: String(summary.absent)})
  }
  if (hasPositiveCount(summary.late)) {
    pairs.push({metric: 'Late', value: String(summary.late)})
  }
  if (hasPositiveCount(summary.leave)) {
    pairs.push({metric: 'Leave', value: String(summary.leave)})
  }
  if (hasPositiveCount(summary.bunk)) {
    pairs.push({metric: 'Bunk', value: String(summary.bunk)})
  }
  if (hasPositiveCount(summary.other)) {
    pairs.push({metric: 'Other', value: String(summary.other)})
  }

  const totalLooksLikeRowCount =
    rowCount > 0 && summary.total === rowCount && !hasMeaningfulStatusCounts(summary)

  if (hasPositiveCount(summary.total) && !totalLooksLikeRowCount) {
    pairs.push({metric: 'Total', value: String(summary.total)})
  }

  const hasStatusCounts = pairs.length > 0

  if (
    hasStatusCounts &&
    summary.presentPercent != null &&
    Number.isFinite(summary.presentPercent)
  ) {
    pairs.push({metric: 'Present %', value: formatPercent(summary.presentPercent)})
  }

  if (hasPositiveCount(summary.totalStudents)) {
    pairs.push({metric: 'Students', value: String(summary.totalStudents)})
  }

  return pairs
}

export function hasDisplayableSummaryKpis(summary: AttendanceSummary, rowCount = 0): boolean {
  return buildSummaryPairs(summary, rowCount).length > 0
}

const MARK_KEYS = ['present', 'absent', 'late', 'leave', 'bunk'] as const

/** A summary whose every attendance mark is zero is the backend saying no attendance was recorded
 *  — only a head-count (`totalStudents`) or the Unmarked tally survives. Those are not attendance,
 *  and a card reading "Students 6" under a narrative that had just said nobody was marked was the
 *  reported defect: the chart was already suppressed for exactly this reason (see
 *  `hasMarkedSegments` in buildV2VisualSummary), and the KPI fallback drew the card anyway. A
 *  summary that carries no breakdown at all (an aggregate with only `total` / `presentPercent`) is
 *  not "no data" — it is left to the rules below. */
function hasRecordedMarks(summary: AttendanceSummary): boolean {
  const known = MARK_KEYS.map((key) => summary[key]).filter(
    (value): value is number => value != null && Number.isFinite(value)
  )
  // No mark counts at all, yet an Unmarked tally: the marks were counted and came to nothing.
  if (known.length === 0) return !hasPositiveCount(summary.other)
  return known.some((value) => value > 0)
}

/** Hide the KPI header when the narrative + table already answer the question, or when there is
 *  no recorded attendance for it to summarise. */
export function shouldShowV2SummaryHeader(
  summary: AttendanceSummary,
  rowCount: number,
  showChart: boolean
): boolean {
  if (showChart) return false
  if (!hasRecordedMarks(summary)) return false
  if (rowCount === 1 && !hasMeaningfulStatusCounts(summary)) return false
  return hasDisplayableSummaryKpis(summary, rowCount)
}

const AttendanceV2SummaryKpis: React.FC<AttendanceV2SummaryKpisProps> = ({
  summary,
  theme,
  rowCount = 0,
}) => {
  const pairs = buildSummaryPairs(summary, rowCount)
  if (!pairs.length) return null

  return <AttendancePlainCard pairs={pairs} theme={theme} />
}

export default AttendanceV2SummaryKpis
